import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import Player from "@vimeo/player";

import { ThemeContext } from "../../../theme/ThemeContext";
import { fetchUserVideoSettings, updateUserAttributes } from "../../../api";

import S from "./VideoModal.module.scss";

interface VideoModalProps {
  videoId: string | null;
  visible: boolean;
  onHide: () => void;
  header?: string;
}

// User's persisted Vimeo playback preferences
interface VideoSettings {
  volume?: number;
  playbackRate?: number;
  textTrack?: string | null;
}

export const VideoModal = ({ videoId, visible, onHide, header = "Video" }: VideoModalProps) => {
  const { appliedTheme } = useContext(ThemeContext);
  const [playerElement, setPlayerElement] = useState<HTMLDivElement | null>(null);
  const playerInstanceRef = useRef<Player | null>(null);
  const videoSettingsRef = useRef<VideoSettings | null>(null);
  const volumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Theme-based colors for Vimeo player
  const colors = useMemo<[string?, string?, string?, string?]>(() => {
    return appliedTheme === "atlas-light"
      ? ["eeeef0", "3b82f6", "1e2126", "eeeef0"]
      : ["282d33", "3b82f6", "ffffff", "282d33"];
  }, [appliedTheme]);

  useEffect(() => {
    if (!videoId || !visible || !playerElement) return;

    const player = new Player(playerElement, {
      id: videoId,
      colors: colors,
    });

    playerInstanceRef.current = player;

    // Load and apply user video settings
    fetchUserVideoSettings().then(settings => {
      if (settings) {
        player.setVolume(settings.volume || 1.0);
        player.setPlaybackRate(settings.playbackRate || 1.0);
        if (settings.textTrack) {
          player.enableTextTrack(settings.textTrack);
        }
      }
      videoSettingsRef.current = settings;
    });

    // Save user preferences when they change
    player.on("volumechange", data => {
      clearTimeout(volumeTimeoutRef.current);
      volumeTimeoutRef.current = setTimeout(() => {
        updateUserAttributes("videoSettings", {
          ...videoSettingsRef.current,
          volume: data.volume,
        });
      }, 250);
    });

    player.on("playbackratechange", data => {
      updateUserAttributes("videoSettings", {
        ...videoSettingsRef.current,
        playbackRate: data.playbackRate,
      });
    });

    player.on("texttrackchange", data => {
      updateUserAttributes("videoSettings", {
        ...videoSettingsRef.current,
        textTrack: data.language,
      });
    });

    return () => {
      if (playerInstanceRef.current) {
        playerInstanceRef.current.destroy();
        playerInstanceRef.current = null;
      }
      clearTimeout(volumeTimeoutRef.current);
    };
  }, [videoId, visible, colors, playerElement]);

  const handleHide = useCallback(() => {
    // Pause video when closing
    if (playerInstanceRef.current) {
      playerInstanceRef.current.pause();
    }
    onHide();
  }, [onHide]);

  return (
    <Dialog
      header={header}
      visible={visible}
      onHide={handleHide}
      style={{ width: "80vw", maxWidth: "1200px" }}
      breakpoints={{ "960px": "90vw", "641px": "95vw" }}
      maximizable
      modal
      contentClassName={S.dialogContent}
    >
      <div className={S.videoContainer} ref={setPlayerElement}></div>
    </Dialog>
  );
};
